import { useState, useEffect, useRef, useMemo } from 'react';
import type { ComparisonPayload, DeckListPayload } from '@sleeved/lib';
import { naturalGridHeight, PROFILE_SIDEBOARD_COLUMN_W, PROFILE_LEFT_COLUMN_H } from '@sleeved/lib';
import { useWsEvents } from '../hooks/useWsEvents';
import { DeckCardGrid } from './DeckCardGrid';
import type { PlayerWidgetData, PlayerPhotoData } from '../types/widgets';

// Overlay tokens — import directly from source, same as DeckCompareOverlay.tsx
import { fonts, glass, glassBorder, glassText, brandGradient } from '../styles/overlayTokens';

// Shared panel module — all panel components, helpers, and constants live here
// to avoid duplication between DeckCompareOverlay and DeckProfileOverlay.
import {
  ChronoCoreRailPanel,
  DigimonRailPanel,
  GundamRailPanel,
  GrandArchiveRailPanel,
  CuratedPlayerPanel,
  computeSharedMax,
  computeLabelUnion,
  computeCoreCostLabelUnion,
  gameNameMap,
  gameLogoMap,
  INITIAL_COMPARISON,
  INITIAL_PLAYER,
  INITIAL_PHOTO,
  type AnimPhase,
  selectRailPanel,
} from './DeckOverlayPanels';

// ---------------------------------------------------------------------------
// Module-level sentinel for the deckList event.
// Declared at module level so it is a stable reference (not re-created per render)
// and satisfies React's stable-initialValue requirement for useWsEvents.
const INITIAL_DECK_LIST: DeckListPayload = { p1: null, p2: null, gameId: '' };

// ---------------------------------------------------------------------------
// Games that support the side-by-side deck profile layout.
// Only games with meaningful non-main zones benefit from the split:
//   grand-archive  → material + sideboard + extra (left) / main (right)
//   chrono-core    → loadout + sideboard (left) / main (right)
// All other games always use the single Grid layout regardless of caster setting.
const SIDE_BY_SIDE_GAMES = new Set(['grand-archive', 'chrono-core']);

// ---------------------------------------------------------------------------
// Per-game card grid column caps.
// DeckCardGrid is game-agnostic; game branching lives here at the call site.
// grand-archive allows 12 because its 80-card decks (10 material + 60 main + 10 side)
// need the extra columns to avoid clipping too many rows at the bottom; at 10 col max
// they exceed the container height and clip excessively. All other games default to 10.
const CARD_GRID_MAX_PER_ROW: Partial<Record<string, number>> = {
  'grand-archive': 12,
};
const CARD_GRID_DEFAULT_MAX_PER_ROW = 10;

// Per-game FORCED column count — render exactly this many columns (bypasses fit-to-fill).
// grand-archive → 12 so its 12-card material zone always sits on a single row; at the
// fit-to-fill's preferred 11 it wraps to 11+1 which reads as broken.
const CARD_GRID_FIXED_PER_ROW: Partial<Record<string, number>> = {
  'grand-archive': 12,
};

// ---------------------------------------------------------------------------
// DeckProfileOverlay — single-deck 1920×1080 showcase overlay.
// Path: /overlay/profile/p1 (coralGlow accent) or /overlay/profile/p2 (brilliantRose accent).
// Both paths use naturalLayout=true so P2 always renders left-to-right.
// A single /overlay/profile prefix check in App.tsx handles both sub-paths.
// ---------------------------------------------------------------------------

export function DeckProfileOverlay() {
  // Slot detection: any non-/p2 path defaults to p1.
  // Memoized with [] — pathname never changes after mount; avoids re-reading window.location on every render.
  const slot = useMemo<'p1' | 'p2'>(
    () => (window.location.pathname.endsWith('/p2') ? 'p2' : 'p1'),
    [],
  );

  const { data: compData } = useWsEvents<ComparisonPayload>('comparison', INITIAL_COMPARISON);
  const { data: playerData } = useWsEvents<PlayerWidgetData>('playerWidget', INITIAL_PLAYER);
  const { data: photoData } = useWsEvents<PlayerPhotoData>('playerPhoto', INITIAL_PHOTO);
  // D91: deck-list subscriptions
  const { data: deckListData } = useWsEvents<DeckListPayload>('deckList', INITIAL_DECK_LIST);

  // D91: per-slot deck-list groups (computed before the content-update effect)
  const slotGroups = slot === 'p1' ? deckListData.p1 : deckListData.p2;

  // Per-slot layout settings sent by the caster. Absent (older payloads) → grid / 5 cols / 4 sideboard default.
  const slotLayoutSettings = slot === 'p1' ? deckListData.p1Layout : deckListData.p2Layout;
  // Effective game id — prefer compData (always set once a room connects) over deckListData.gameId
  // (can be '' before the first deckList event arrives).
  const effectiveGameId = compData.gameId || deckListData.gameId;
  // Side-by-side only for supported games AND when the caster explicitly chose that layout.
  const useSideBySide =
    SIDE_BY_SIDE_GAMES.has(effectiveGameId) &&
    slotLayoutSettings?.layout === 'side-by-side';
  const mainDeckColumns = slotLayoutSettings?.mainDeckColumns ?? 5;
  // sideboardColumns: absent on older payloads → 4 (backward compat).
  const sideboardColumns = slotLayoutSettings?.sideboardColumns ?? 4;

  // Per-game column cap for the card grid — game branching is the call site's job;
  // DeckCardGrid itself is game-agnostic and receives maxPerRow as a plain number.
  // Key on compData.gameId (the authoritative game that also drives rail-panel selection);
  // deckListData.gameId can be '' before/independent of the deckList event, which would
  // silently drop grand-archive back to the default 10. Fall back to deckListData.gameId.
  const cardGridMaxPerRow =
    CARD_GRID_MAX_PER_ROW[compData.gameId] ??
    CARD_GRID_MAX_PER_ROW[deckListData.gameId] ??
    CARD_GRID_DEFAULT_MAX_PER_ROW;
  // Forced exact column count for games that need it (grand-archive → 12). undefined = fit-to-fill.
  // Only applies in grid mode; side-by-side uses fixedPerRow per-column from layout settings.
  const cardGridFixedPerRow =
    CARD_GRID_FIXED_PER_ROW[compData.gameId] ?? CARD_GRID_FIXED_PER_ROW[deckListData.gameId];

  // Animation phase: 'hidden' → (rAF) → 'entering' | 'static'
  const [phase, setPhase] = useState<AnimPhase>(() =>
    new URLSearchParams(window.location.search).get('static') === 'true' ? 'static' : 'hidden',
  );

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  // Entrance: trigger after one rAF to prevent one-frame flash.
  useEffect(() => {
    if (phase !== 'hidden') return;
    const rafId = requestAnimationFrame(() => setPhase('entering'));
    return () => cancelAnimationFrame(rafId);
  }, [phase]);

  // Content update: when deck stats change while the overlay is already visible,
  // fade in the slot panel's content. Does NOT re-run the entrance sequence.
  // The `getElementById` null-check below is a defensive safety net for race
  // conditions during phase transitions before the element is mounted.
  const prevCompRef = useRef<ComparisonPayload>(INITIAL_COMPARISON);
  useEffect(() => {
    const prev = prevCompRef.current;
    prevCompRef.current = compData;

    // Skip on initial data load (INITIAL_COMPARISON sentinel) and in static mode.
    // 'entering' is the steady state in normal mode.
    if (phase !== 'entering' || prev === INITIAL_COMPARISON) return;

    const prevSlotStats = slot === 'p1' ? prev.p1 : prev.p2;
    const currSlotStats = slot === 'p1' ? compData.p1 : compData.p2;
    if (prevSlotStats === currSlotStats) return;

    const el = document.getElementById(`dcao-content-${slot}`);
    if (!el) return; // defensive: element may not be in DOM during a phase transition

    el.classList.remove('dcao-content-update');
    const rafId = requestAnimationFrame(() => el.classList.add('dcao-content-update'));
    const timerId = setTimeout(() => el.classList.remove('dcao-content-update'), 350);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
    };
  }, [compData, phase, slot]);

  // ── Slot-scoped data ────────────────────────────────────────────────────────
  const slotStats = slot === 'p1' ? compData.p1 : compData.p2;
  const slotPlayer = slot === 'p1' ? playerData.p1 : playerData.p2;
  const slotPhoto = slot === 'p1' ? photoData.p1Photo : photoData.p2Photo;

  // selectRailPanel returns the per-game condensed rail panel or null (falls back to CuratedPlayerPanel).
  const RailPanelComponent = selectRailPanel(compData.gameId);
  const gameName = gameNameMap[compData.gameId] ?? compData.gameId;

  // ── Shared-scale computations (single-slot: peer is always null) ─────────────
  // costCurve shared max / label union — peer=null since no peer panel is shown.
  const sharedMax = computeSharedMax(slotStats, null);
  const labelUnion = computeLabelUnion(slotStats, null);

  // Core Cost Distribution (secondaryCurve) — ChronoCore only.
  const coreCostLabelUnion = computeCoreCostLabelUnion(slotStats, null);
  const coreCostSharedMax = Math.max(...(slotStats?.secondaryCurve ?? []).map(d => d.count), 1);

  // Damage / Charge heatmap shared maxima — single-slot: peer is always null so
  // we max over slotStats only. Mirrors the derivation used in DeckCompareOverlay.
  const dmgSharedMax = Math.max(
    ...(slotStats?.additionalCurves?.find(c => /damage/i.test(c.label))?.data ?? []).map(d => d.count),
    1,
  );
  const chargeSharedMax = Math.max(
    ...(slotStats?.additionalCurves?.find(c => /charge/i.test(c.label))?.data ?? []).map(d => d.count),
    1,
  );

  // ── Per-game shared-scale computations (single-slot) ─────────────────────
  // Digimon: level/form distribution (costCurve) — alias sharedMax/labelUnion
  const digimonLevelSharedMax = sharedMax;
  const digimonLevelLabelUnion = labelUnion;

  // Gundam: level distribution (costCurve) — alias sharedMax; cost distribution (secondaryCurve)
  const gundamLevelSharedMax = sharedMax;
  const gundamLevelLabelUnion = labelUnion;
  const gundamCostSharedMax = Math.max(...(slotStats?.secondaryCurve ?? []).map(d => d.count), 1);
  const gundamCostLabelUnion = computeCoreCostLabelUnion(slotStats, null);

  // Grand Archive: combined cost curve (memory + reserve) — max and label union over slotStats only
  const gaCombinedCostSharedMax = Math.max(
    ...(slotStats?.combinedCostCurve ?? []).flatMap(s => s.data.map(d => d.count)),
    1,
  );
  const gaCombinedCostLabelUnion = (() => {
    const allLabels = new Set<string>();
    for (const series of slotStats?.combinedCostCurve ?? []) {
      for (const d of series.data) { allLabels.add(d.label); }
    }
    return [...allLabels].sort((a, b) => Number(a) - Number(b));
  })();

  const placeholderText =
    slot === 'p1'
      ? "Load Player 1's deck in the caster console"
      : "Load Player 2's deck in the caster console";

  const isHidden = phase === 'hidden';

  return (
    <div
      aria-hidden="true"
      style={{
        width: 1920,
        height: 1080,
        position: 'relative',
        background: 'transparent',
        fontFamily: fonts.body,
        overflow: 'hidden',
        color: glassText.primary,
        // Hide root until rAF fires to prevent one-frame flash (static mode: always visible).
        opacity: isHidden && !prefersReducedMotion.current ? 0 : 1,
        // Reduced motion: fade the root in as a unit instead of staggered per-element.
        transition: prefersReducedMotion.current && !isHidden ? 'opacity 150ms ease-out' : undefined,
      }}
    >
      {/* Glass card 1840×1000 at 40px inset */}
      <div
        style={{
          position: 'absolute',
          inset: 40,
          background: glass.surfaceHeavy,
          borderRadius: 20,
          border: `1px solid ${glassBorder.light}`,
          boxShadow: '0 0 80px rgba(0,0,0,0.5), 0 8px 40px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top strip (100px) — Sleeved lockup left, game logo right */}
        <div
          style={{
            height: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 40px',
            borderBottom: `1px solid ${glassBorder.subtle}`,
            flexShrink: 0,
          }}
        >
          {/* Left — Sleeved lockup */}
          <img src="/lockup.png" alt="Sleeved" style={{ height: 80, width: 'auto' }} />

          {/* Center — empty; reserved for future round/event label */}
          <span />

          {/* Right — game logo image (known games) or text pill fallback */}
          {gameLogoMap[compData.gameId] ? (
            <img
              src={gameLogoMap[compData.gameId]}
              alt={gameName}
              style={{ height: 80, width: 'auto', display: 'block' }}
            />
          ) : (
            <span
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: glassText.muted,
                background: glassBorder.subtle,
                borderRadius: 100,
                padding: '4px 14px',
              }}
            >
              {gameName}
            </span>
          )}
        </div>

        {/* Main content area */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            // Reduced vertical padding to give panel more height; trimmed from 18px to 10px
            // to recover 16px of headroom for worst-case ChronoCore keyword cloud at 1400px width.
            padding: '10px 40px',
            gap: RailPanelComponent !== null ? 16 : 0,
            minHeight: 0,
          }}
        >
          {RailPanelComponent !== null ? (
            // ── Per-game: fixed 25 / 75 split — condensed stats rail + card grid ──
            // Left column always shows per-game rail stats; right column shows the card grid.
            <>
              {/* Left ~25%: condensed stats rail. naturalLayout is implicit (rail panels always left-to-right). */}
              <div style={{ flexBasis: '25%', flexShrink: 0, minHeight: 0, display: 'flex' }}>
                {(() => {
                  const common = { side: slot, stats: slotStats, peerHasStats: false as const, playerName: slotPlayer.name, archetype: slotPlayer.archetype, phase, reducedMotion: prefersReducedMotion.current, placeholderText };
                  switch (compData.gameId) {
                    case 'chrono-core':
                      return (
                        <ChronoCoreRailPanel
                          {...common}
                          pilotCardUrl={slotPlayer.pilotCardUrl}
                          coreCardUrl={slotPlayer.coreCardUrl}
                          weaponIdentity={slotPlayer.weaponIdentity}
                          equipment={slotPlayer.equipment}
                          pilotLife={slotPlayer.pilotLife}
                          coreShield={slotPlayer.coreShield}
                          coreCostSharedMax={coreCostSharedMax}
                          coreCostLabelUnion={coreCostLabelUnion}
                          dmgSharedMax={dmgSharedMax}
                          chargeSharedMax={chargeSharedMax}
                        />
                      );
                    case 'digimon':
                      return (
                        <DigimonRailPanel
                          {...common}
                          featuredCardImageUrl={slotPlayer.featuredCardImageUrl}
                          levelSharedMax={digimonLevelSharedMax}
                          levelLabelUnion={digimonLevelLabelUnion}
                        />
                      );
                    case 'gundam':
                      return (
                        <GundamRailPanel
                          {...common}
                          featuredCardImageUrl={slotPlayer.featuredCardImageUrl}
                          levelSharedMax={gundamLevelSharedMax}
                          levelLabelUnion={gundamLevelLabelUnion}
                          costSharedMax={gundamCostSharedMax}
                          costLabelUnion={gundamCostLabelUnion}
                        />
                      );
                    case 'grand-archive':
                      return (
                        <GrandArchiveRailPanel
                          {...common}
                          gaIdentitySpiritUrl={slotPlayer.gaIdentitySpiritUrl}
                          gaIdentityChampionUrl={slotPlayer.gaIdentityChampionUrl}
                          combinedCostSharedMax={gaCombinedCostSharedMax}
                          combinedCostLabelUnion={gaCombinedCostLabelUnion}
                        />
                      );
                    default:
                      if (import.meta.env.DEV) console.warn(`[DeckProfileOverlay] selectRailPanel returned a panel for gameId "${compData.gameId}" but the inner switch has no case — falling back to CuratedPlayerPanel`);
                      return (
                        <CuratedPlayerPanel
                          side={slot}
                          stats={slotStats}
                          peerHasStats={false}
                          playerName={slotPlayer.name}
                          archetype={slotPlayer.archetype}
                          photoUrl={slotPhoto}
                          sharedMax={sharedMax}
                          labelUnion={labelUnion}
                          colorLabel={compData.colorLabel}
                          phase={phase}
                          reducedMotion={prefersReducedMotion.current}
                          fullWidth
                          naturalLayout
                          placeholderText={placeholderText}
                        />
                      );
                  }
                })()}
              </div>

              {/* Right ~75%: card grid. flex:1 + minWidth:0 so DeckCardGrid's
                  outerRef.clientWidth measures the correct available width. */}
              <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                {slotGroups ? (
                  useSideBySide ? (
                    // Two-column side-by-side layout driven by caster setting.
                    // Both grand-archive and chrono-core use 'main' as the main-deck zone id.
                    //
                    // Left column: two stacked grids with a divider between them.
                    //   Top grid  — non-main, non-sideboard zones: GA gets material + extra
                    //               (extra zone id is 'extra', carries Mastery/Token cards);
                    //               chrono-core gets loadout. Fixed 4 per row.
                    //   Divider   — thin horizontal rule (glassBorder.medium).
                    //   Bottom grid — sideboard zone only (zoneId === 'sideboard'), hidden when
                    //               the deck has no sideboard. Column count from sideboardColumns setting (4 or 5).
                    //
                    // Right column: main zone only, column count from mainDeckColumns setting (4–6).
                    (() => {
                      const materialGroups = slotGroups.filter((g) => g.zoneId !== 'main' && g.zoneId !== 'sideboard');
                      const sideboardGroups = slotGroups.filter((g) => g.zoneId === 'sideboard');
                      const hasSideboard = sideboardGroups.length > 0;
                      // Size each left-column section to its NATURAL content height so a sparse
                      // section (e.g. a Chrono Core loadout of ~5 cards) packs tight against the
                      // divider instead of being stretched and leaving dead space. If the two
                      // sections + divider would overflow the column, scale both down uniformly.
                      const materialCount = materialGroups.reduce((s, g) => s + g.cards.length, 0);
                      const sideboardCount = sideboardGroups.reduce((s, g) => s + g.cards.length, 0);
                      const DIVIDER_TOTAL_H = 11; // 1px rule + 5px top + 5px bottom margin
                      const matNaturalH = naturalGridHeight(materialCount, 4, PROFILE_SIDEBOARD_COLUMN_W);
                      const sbNaturalH = hasSideboard
                        ? naturalGridHeight(sideboardCount, sideboardColumns, PROFILE_SIDEBOARD_COLUMN_W)
                        : 0;
                      const totalNaturalH = matNaturalH + sbNaturalH + (hasSideboard ? DIVIDER_TOTAL_H : 0);
                      const fitScale = totalNaturalH > PROFILE_LEFT_COLUMN_H ? PROFILE_LEFT_COLUMN_H / totalNaturalH : 1;
                      const matSectionH = matNaturalH * fitScale;
                      const sbSectionH = sbNaturalH * fitScale;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'row', gap: 16, width: '100%', height: '100%', minHeight: 0 }}>
                          {/* Left column: material/loadout/extra (top) + optional sideboard (bottom),
                              each sized to content and top-anchored so any slack sits at the bottom. */}
                          <div style={{ flex: '0 0 38%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                            {/* Top: material + extra (GA) or loadout (CC). Hard-locked to 4 columns;
                                sized to content (scaled to fit if needed). */}
                            <div style={{ height: matSectionH, minHeight: 0, minWidth: 0, flexShrink: 0 }}>
                              <DeckCardGrid
                                groups={materialGroups}
                                gameId={deckListData.gameId}
                                fixedPerRow={4}
                              />
                            </div>
                            {/* Divider + sideboard grid — only when the deck has a sideboard */}
                            {hasSideboard && (
                              <>
                                <div style={{
                                  height: 1,
                                  flexShrink: 0,
                                  background: glassBorder.medium,
                                  margin: '5px 0',
                                }} />
                                {/* Bottom: sideboard only — exact column count from the caster
                                    sideboardColumns control (4/5); sized to content (scaled to fit). */}
                                <div style={{ height: sbSectionH, minHeight: 0, minWidth: 0, flexShrink: 0 }}>
                                  <DeckCardGrid
                                    groups={sideboardGroups}
                                    gameId={deckListData.gameId}
                                    fixedPerRow={sideboardColumns}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                          {/* Right column: main deck — exact column count from the caster
                              mainDeckColumns control (4–6); cards bounded to fit the viewport (no cut-off). */}
                          <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                            <DeckCardGrid
                              groups={slotGroups.filter((g) => g.zoneId === 'main')}
                              gameId={deckListData.gameId}
                              fixedPerRow={mainDeckColumns}
                            />
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <DeckCardGrid
                      groups={slotGroups}
                      gameId={deckListData.gameId}
                      maxPerRow={cardGridMaxPerRow}
                      fixedPerRow={cardGridFixedPerRow}
                    />
                  )
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    color: glassText.secondary,
                    fontSize: 16,
                    textAlign: 'center',
                  }}>
                    {placeholderText}
                  </div>
                )}
              </div>
            </>
          ) : (
            // ── Unrecognized game: fixed full-width stats panel ──
            <CuratedPlayerPanel
              side={slot}
              stats={slotStats}
              peerHasStats={false}
              playerName={slotPlayer.name}
              archetype={slotPlayer.archetype}
              photoUrl={slotPhoto}
              sharedMax={sharedMax}
              labelUnion={labelUnion}
              colorLabel={compData.colorLabel}
              phase={phase}
              reducedMotion={prefersReducedMotion.current}
              fullWidth
              naturalLayout
              placeholderText={placeholderText}
            />
          )}
        </div>

        {/* Bottom accent bar — brandGradient, 4px, same pattern as DeckCompareOverlay */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 4,
            background: brandGradient.horizontal,
          }}
        />
      </div>
    </div>
  );
}
